/**
 * Universal Action Button Component
 * Unified button styles for Create/Add, Edit, and Delete actions across all dashboards
 */

import React from 'react';
import { theme } from '../../theme';

export type ActionButtonVariant =
  | 'create'
  | 'edit'
  | 'delete'
  | 'save'
  | 'cancel'
  | 'submit'
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'export'
  | 'ghost'
  | 'icon'
  | 'tab'
  | 'link';
export type ActionButtonSize = 'small' | 'medium' | 'large';

interface ActionButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  variant: ActionButtonVariant;
  size?: ActionButtonSize;
  children: React.ReactNode;
  loading?: boolean;
  fullWidth?: boolean;
  customStyle?: React.CSSProperties;
  /** For tab variant: indicates if the tab is currently active */
  active?: boolean;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  variant,
  size = 'medium',
  children,
  loading = false,
  disabled = false,
  fullWidth = false,
  customStyle = {},
  active = false,
  ...props
}) => {
  // Get size configuration from theme
  const sizeConfig = theme.sizes.getSizeConfig(size);

  // Icon variant needs square padding (equal on all sides)
  const iconPadding: Record<ActionButtonSize, string> = {
    small: '8px',
    medium: '10px',
    large: '12px',
  };

  // Icon variant minimum dimensions for square shape (also ensures mobile touch targets)
  const iconMinSize: Record<ActionButtonSize, number> = {
    small: 44, // Minimum 44px for mobile touch target
    medium: 44,
    large: 48,
  };

  // Base button styles using theme (NO customStyle here!)
  const baseStyle: React.CSSProperties = {
    borderRadius: theme.borders.button.radius,
    fontWeight: theme.typography.fontWeight.bold,
    fontFamily: theme.typography.fontFamily.primary,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    transition: `all ${theme.animations.duration.normal}ms ${theme.animations.easing.ease}`,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing[2],
    outline: 'none',
    position: 'relative',
    border: 'none',
    width: fullWidth ? '100%' : 'auto',
    boxSizing: 'border-box',
    ...sizeConfig,
  };

  // Color variants using theme colors (Style Guide compliant)
  const variantStyles: Record<ActionButtonVariant, React.CSSProperties> = {
    // Primary/Action buttons - deep scarlet fill + white text. `brand.fill` (not
    // `brand.primary`) so white clears WCAG AA (4.8:1) at every size in both themes.
    create: {
      backgroundColor: theme.colors.brand.fill,
      color: theme.colors.text.onAccent,
      border: '1px solid transparent',
    },
    primary: {
      backgroundColor: theme.colors.brand.fill,
      color: theme.colors.text.onAccent,
      border: '1px solid transparent',
    },
    submit: {
      backgroundColor: theme.colors.brand.fill,
      color: theme.colors.text.onAccent,
      border: '1px solid transparent',
    },
    save: {
      backgroundColor: theme.colors.status.success,
      color: '#000000',
      border: '1px solid transparent',
    },
    // Secondary buttons - White background + black text (Style Guide)
    secondary: {
      backgroundColor: '#FFFFFF',
      color: '#000000',
      border: `1px solid ${theme.colors.border.default}`,
    },
    export: {
      backgroundColor: theme.colors.brand.fill,
      color: theme.colors.text.onAccent,
      border: '1px solid transparent',
    },
    tertiary: {
      backgroundColor: theme.colors.background.tertiary,
      color: theme.colors.text.primary,
      border: `1px solid ${theme.colors.border.default}`,
    },
    // Edit/Cancel — neutral surface + primary text (white in dark, ink in light)
    // and a visible border so the control reads on either ground.
    edit: {
      backgroundColor: theme.colors.background.quaternary,
      color: theme.colors.text.primary,
      border: `1px solid ${theme.colors.border.light}`,
    },
    cancel: {
      backgroundColor: theme.colors.background.quaternary,
      color: theme.colors.text.primary,
      border: `1px solid ${theme.colors.border.default}`,
    },
    // Delete button - Dark red background + white text
    delete: {
      backgroundColor: theme.colors.status.error,
      color: '#FFFFFF',
      border: '1px solid transparent',
    },
    // Ghost button - Transparent with border
    ghost: {
      backgroundColor: 'transparent',
      color: theme.colors.text.secondary,
      border: `1px solid ${theme.colors.border.default}`,
    },
    // Icon button - Square icon-only button
    icon: {
      backgroundColor: 'transparent',
      color: theme.colors.text.secondary,
      border: 'none',
      padding: iconPadding[size],
      minWidth: iconMinSize[size],
      minHeight: iconMinSize[size],
      borderRadius: theme.borders.button.radius,
    },
    // Tab button - Navigation tab style
    tab: {
      backgroundColor: 'transparent',
      color: active ? theme.colors.brand.primary : theme.colors.text.secondary,
      border: 'none',
      borderBottom: active ? `2px solid ${theme.colors.brand.primary}` : '2px solid transparent',
      borderRadius: 0,
    },
    // Link button - Text-only link style
    link: {
      backgroundColor: 'transparent',
      color: theme.colors.brand.primary,
      border: 'none',
      padding: '0',
      minHeight: 'auto',
    },
  };

  // Hover styles using theme colors (Style Guide compliant)
  const hoverStyles: Record<ActionButtonVariant, React.CSSProperties> = {
    // Primary buttons hover - Darker orange
    create: {
      backgroundColor: theme.colors.brand.fillHover,
    },
    primary: {
      backgroundColor: theme.colors.brand.fillHover,
    },
    submit: {
      backgroundColor: theme.colors.brand.fillHover,
    },
    save: {
      backgroundColor: '#45A049',
    },
    // Secondary buttons hover - Light gray background
    secondary: {
      backgroundColor: '#F5F5F5',
      borderColor: theme.colors.border.light,
    },
    export: {
      backgroundColor: theme.colors.brand.fillHover,
    },
    // Tertiary buttons hover - Lighter dark gray
    tertiary: {
      backgroundColor: theme.colors.background.quaternary,
    },
    edit: {
      backgroundColor: theme.colors.background.elevated,
      borderColor: theme.colors.border.lighter,
    },
    cancel: {
      backgroundColor: theme.colors.background.quaternary,
    },
    // Delete button hover - Darker red
    delete: {
      backgroundColor: '#B91C1C', // Darker red
    },
    // Ghost button hover
    ghost: {
      backgroundColor: theme.colors.background.tertiary,
    },
    // Icon button hover
    icon: {
      backgroundColor: theme.colors.background.tertiary,
      color: theme.colors.text.primary,
    },
    // Tab button hover
    tab: {
      color: theme.colors.brand.primary,
    },
    // Link button hover - underline
    link: {
      textDecoration: 'underline',
    },
  };

  // Active styles using theme colors (Style Guide compliant)
  const activeStyles: Record<ActionButtonVariant, React.CSSProperties> = {
    // Primary buttons active - Darkest orange
    create: {
      backgroundColor: theme.colors.brand.fillActive,
    },
    primary: {
      backgroundColor: theme.colors.brand.fillActive,
    },
    submit: {
      backgroundColor: theme.colors.brand.fillActive,
    },
    save: {
      backgroundColor: '#3D8B40',
    },
    // Secondary buttons active - Medium gray background
    secondary: {
      backgroundColor: '#E0E0E0',
    },
    export: {
      backgroundColor: theme.colors.brand.fillActive,
    },
    // Tertiary buttons active - More elevated dark gray
    tertiary: {
      backgroundColor: theme.colors.background.elevated,
    },
    edit: {
      backgroundColor: '#444444',
      borderColor: theme.colors.border.lighter,
    },
    cancel: {
      backgroundColor: theme.colors.background.elevated,
    },
    // Delete button active - Darkest red
    delete: {
      backgroundColor: '#991B1B',
    },
    // Ghost button active
    ghost: {
      backgroundColor: theme.colors.background.quaternary,
    },
    // Icon button active
    icon: {
      backgroundColor: theme.colors.background.quaternary,
      color: theme.colors.text.primary,
    },
    // Tab button active (mouse down)
    tab: {
      color: theme.colors.brand.dark,
    },
    // Link button active
    link: {
      color: theme.colors.brand.dark,
      textDecoration: 'underline',
    },
  };

  // Disabled styles - High contrast text on muted backgrounds
  const disabledStyles: Record<ActionButtonVariant, React.CSSProperties> = {
    // Primary/Action buttons disabled - Muted orange with readable dark text
    create: {
      backgroundColor: '#8B6B5A',
      color: '#1A1A1A',
      border: '1px solid transparent',
    },
    primary: {
      backgroundColor: '#8B6B5A',
      color: '#1A1A1A',
      border: '1px solid transparent',
    },
    submit: {
      backgroundColor: '#8B6B5A',
      color: '#1A1A1A',
      border: '1px solid transparent',
    },
    save: {
      backgroundColor: '#5A7A5A',
      color: '#1A1A1A',
      border: '1px solid transparent',
    },
    secondary: {
      backgroundColor: '#CCCCCC',
      color: '#666666',
      border: `1px solid ${theme.colors.border.default}`,
    },
    export: {
      backgroundColor: '#8B6B5A',
      color: '#1A1A1A',
      border: '1px solid transparent',
    },
    tertiary: {
      backgroundColor: '#2A2A2A',
      color: '#666666',
      border: '1px solid transparent',
    },
    // Edit/Cancel buttons disabled - Dark background with muted text
    edit: {
      backgroundColor: '#333333',
      color: '#888888',
      border: `1px solid ${theme.colors.border.default}`,
    },
    cancel: {
      backgroundColor: '#333333',
      color: '#888888',
      border: '1px solid transparent',
    },
    delete: {
      backgroundColor: '#5A3A3A',
      color: '#AAAAAA',
      border: '1px solid transparent',
    },
    // Ghost button disabled
    ghost: {
      backgroundColor: 'transparent',
      color: theme.colors.text.quaternary,
      border: `1px solid ${theme.colors.border.default}`,
    },
    // Icon button disabled
    icon: {
      backgroundColor: 'transparent',
      color: theme.colors.text.quaternary,
      border: 'none',
    },
    // Tab button disabled
    tab: {
      backgroundColor: 'transparent',
      color: theme.colors.text.quaternary,
      border: 'none',
      borderBottom: '2px solid transparent',
    },
    // Link button disabled
    link: {
      backgroundColor: 'transparent',
      color: theme.colors.text.quaternary,
      border: 'none',
    },
  };

  // Apply appropriate styles based on disabled state
  const stateStyles = disabled || loading ? disabledStyles[variant] : variantStyles[variant];

  // Combine styles: base -> variant -> customStyle (customStyle wins for overrides)
  const combinedStyle: React.CSSProperties = {
    ...baseStyle,
    ...stateStyles, // Apply variant colors or disabled colors
    ...customStyle, // Apply ALL custom styles last so they can override variant styles
  };

  // When customStyle provides color overrides, skip hover/active state changes
  // to preserve the custom appearance
  const hasCustomColors = customStyle.backgroundColor || customStyle.color || customStyle.border;

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !loading && !hasCustomColors) {
      Object.assign(e.currentTarget.style, hoverStyles[variant]);
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !loading && !hasCustomColors) {
      Object.assign(e.currentTarget.style, variantStyles[variant]);
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !loading && !hasCustomColors) {
      Object.assign(e.currentTarget.style, activeStyles[variant]);
    }
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !loading && !hasCustomColors) {
      Object.assign(e.currentTarget.style, hoverStyles[variant]);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLButtonElement>) => {
    if (!disabled && !loading) {
      e.currentTarget.style.boxShadow = theme.focusRing.default.boxShadow;
    }
    props.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLButtonElement>) => {
    if (!disabled && !loading) {
      e.currentTarget.style.boxShadow = 'none';
    }
    props.onBlur?.(e);
  };

  return (
    <button
      type="button"
      style={combinedStyle}
      disabled={disabled || loading}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onFocus={handleFocus}
      onBlur={handleBlur}
      {...props}
    >
      {loading ? (
        <>
          <span className="action-button-spinner" />
          Loading...
        </>
      ) : (
        children
      )}
    </button>
  );
};

export default ActionButton;
